import React from 'react';
import { motion } from 'framer-motion';
import { Leaf, Instagram, Youtube, Twitter, Facebook, Mail, Phone, MapPin } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', href: '#home' },
    { name: 'Plant Info', href: '#plants' },
    { name: 'How-to Guides', href: '#guides' },
    { name: 'Placement Tips', href: '#placement' }
  ];

  const resources = [
    { name: 'Plant Care Calendar', href: '#' },
    { name: 'Troubleshooting Guide', href: '#' },
    { name: 'Plant Identification', href: '#' },
    { name: 'Community Forum', href: '#' }
  ];

  const socialLinks = [
    { name: 'Instagram', icon: Instagram, href: '#', color: 'hover:text-pink-400' },
    { name: 'YouTube', icon: Youtube, href: '#', color: 'hover:text-red-400' },
    { name: 'Twitter', icon: Twitter, href: '#', color: 'hover:text-blue-400' },
    { name: 'Facebook', icon: Facebook, href: '#', color: 'hover:text-blue-600' }
  ];

  const scrollToSection = (href) => {
    if (href.startsWith('#')) {
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <footer id="contact" className="bg-gray-50 text-gray-800 border-t border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand Section */}
          <motion.div
            className="lg:col-span-1"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <div className="flex items-center space-x-3 mb-4">
              <Leaf className="h-8 w-8 text-primary-500" />
              <span className="text-2xl font-bold text-gray-800">Botanical Buzz</span>
            </div>
            <p className="text-gray-600 mb-6 leading-relaxed text-sm">
              Helping Plants & People Thrive. Your trusted companion for creating beautiful, healthy green spaces.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3">
              <div className="flex items-center text-gray-600 hover:text-gray-900 transition-colors">
                <Mail className="h-4 w-4 mr-3 text-primary-500" />
                <span className="text-sm">hello@botanicalbuzz.com</span>
              </div>
              <div className="flex items-center text-gray-600 hover:text-gray-900 transition-colors">
                <Phone className="h-4 w-4 mr-3 text-primary-500" />
                <span className="text-sm">+1 (555) 123-PLANT</span>
              </div>
              <div className="flex items-center text-gray-600 hover:text-gray-900 transition-colors">
                <MapPin className="h-4 w-4 mr-3 text-primary-500" />
                <span className="text-sm">Green Valley, CA</span>
              </div>
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <h3 className="text-lg font-semibold mb-4 text-gray-800">Quick Links</h3>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <motion.button
                    onClick={() => scrollToSection(link.href)}
                    className="text-gray-600 hover:text-gray-900 transition-colors duration-200 text-sm"
                    whileHover={{ x: 5 }}
                  >
                    {link.name}
                  </motion.button>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Resources */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <h3 className="text-lg font-semibold mb-4 text-gray-800">Resources</h3>
            <ul className="space-y-3">
              {resources.map((resource) => (
                <li key={resource.name}>
                  <motion.a
                    href={resource.href}
                    className="text-gray-600 hover:text-gray-900 transition-colors duration-200 text-sm"
                    whileHover={{ x: 5 }}
                  >
                    {resource.name}
                  </motion.a>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Social & Newsletter */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
          >
            <h3 className="text-lg font-semibold mb-4 text-gray-800">Connect</h3>
            <div className="flex space-x-4 mb-6">
              {socialLinks.map((social) => {
                const IconComponent = social.icon;
                return (
                  <motion.a
                    key={social.name}
                    href={social.href}
                    className={`text-gray-600 ${social.color} transition-colors duration-200`}
                    whileHover={{ scale: 1.2, y: -2, color: '#16a34a' }}
                    whileTap={{ scale: 0.9 }}
                  >
                    <IconComponent className="h-6 w-6" />
                  </motion.a>
                );
              })}
            </div>
            
            <p className="text-gray-600 text-sm mb-4">
              Join our newsletter for daily plant inspiration and tips!
            </p>
            
            <motion.button
              onClick={() => scrollToSection('#newsletter')}
              className="group bg-gradient-to-r from-primary-500 to-green-500 hover:from-primary-600 hover:to-green-600 text-white px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105"
              whileTap={{ scale: 0.95 }}
            >
              Join Newsletter
            </motion.button>
          </motion.div>
        </div>

        {/* Bottom Section */}
        <motion.div
          className="border-t border-gray-200 mt-12 pt-8"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="text-gray-500 text-sm mb-4 md:mb-0">
              © {currentYear} Botanical Buzz. All rights reserved.
            </div>
            <div className="flex space-x-6 text-sm">
              <a href="#" className="text-gray-500 hover:text-gray-900 transition-colors duration-200">
                Privacy Policy
              </a>
              <a href="#" className="text-gray-500 hover:text-gray-900 transition-colors duration-200">
                Terms of Service
              </a>
              <a href="#" className="text-gray-500 hover:text-gray-900 transition-colors duration-200">
                Cookie Policy
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
